/**
 * Welke zetten mag deze speler nu doen?
 *
 * Dit is regelkennis, geen tactiek: de lijst zegt wat *mag*, niet wat *slim is*. De
 * computer weegt hem in `packages/ai`, het scherm gebruikt hem om te bepalen welke
 * knoppen aan mogen en welke plekken oplichten.
 *
 * Hij hoort hier omdat de server hem meestuurt. Zolang de browser hem zelf uitrekende,
 * had die de volledige spelstand nodig — inclusief de handkaarten van de tegenstander.
 * Dat kan niet over de lijn, en daarmee was dit de laatste knoop die het scherm aan de
 * spelstand vastklonk.
 *
 * **Eén zet staat er met opzet niet in: een ruilaanbod.** Wát je aanbiedt is geen keuze
 * uit een lijstje maar iets wat je samenstelt — de mens in het ruilscherm, de computer
 * met zijn eigen afweging. Een lijst van alle denkbare bundels zou bovendien enorm zijn.
 */
import { BASIC_CARDS, LANDSCAPE_RESOURCE, type CardCost, type Resource, type Seat } from '@catan/contracts';
import {
  actionCardName,
  destructibleBuildings,
  isCounterOnly,
  knightsOf,
} from './actioncards.js';
import { roadSpots, settlementSpots, type Payment } from './building.js';
import { terugnameKandidaten } from './turn.js';
import { isLegalAction, tradeAbilitiesOf, type Action } from './game.js';
import type { CardTarget } from './playcard.js';
import type { GameState, PlayerState, Side } from './state.js';
import { tradableWithBank } from './trading.js';

const costOf = (naam: string): CardCost => {
  const card = BASIC_CARDS.find((c) => c.name === naam);
  if (card === undefined) throw new Error(`kaart ${naam} ontbreekt`);
  return card.cost;
};

/**
 * Zoekt landschappen om mee te betalen: pak van elke gevraagde grondstof net zoveel
 * als nodig, van de landschappen waar het meest ligt (dan raakt er niets vol te vroeg
 * op). Geeft undefined als het niet lukt.
 */
export function findPayment(player: PlayerState, cost: CardCost): Payment | undefined {
  const payment: { column: number; side: Side }[] = [];

  for (const [resource, needed] of Object.entries(cost) as [Resource, number][]) {
    const bruikbaar = player.principality.landscapes
      .filter((t) => LANDSCAPE_RESOURCE[t.landscape] === resource && t.resources > 0)
      .sort((a, b) => b.resources - a.resources);

    let rest = needed;
    for (const tile of bruikbaar) {
      const pak = Math.min(rest, tile.resources);
      for (let i = 0; i < pak; i++) payment.push({ column: tile.column, side: tile.side });
      rest -= pak;
      if (rest === 0) break;
    }
    if (rest > 0) return undefined;
  }
  return payment;
}

/**
 * Wie moet er nú iets doen?
 *
 * Drie gevallen, in deze volgorde. Staat er een vraag open, dan is het degene aan wie hij
 * gesteld is. Ligt er een aanbod, dan is het de **ander** — een aanbod doe je in je eigen
 * beurt, dus de antwoorder is per definitie niet aan de beurt. En anders is het gewoon de
 * beurtspeler.
 *
 * Die middelste regel stond nergens, en dat kostte wat. Een speellus die alleen naar
 * `pending` en `turn` keek — zoals de simulatie en de doormeting deden — gaf de antwoorder
 * nooit het woord: de computer deed zevenentwintig aanbiedingen per potje en er werd er
 * geen enkele beantwoord. Ruilen tússen spelers stond dus in geen enkele simulatie, terwijl
 * het scherm het wél goed deed.
 */
export function aanZet(state: GameState): Seat {
  if (state.pending !== undefined) return state.pending.seat;
  if (state.offer !== undefined) return state.offer.from === 0 ? 1 : 0;
  return state.turn;
}

/** Alle zetten die deze speler nu mag doen. */
export function legalActions(state: GameState, seat: Seat): Action[] {
  const player = state.players[seat];
  const kandidaten: Action[] = [];

  if (state.pending !== undefined) {
    const pending = state.pending;
    if (pending.seat !== seat) return [];

    // Komt er een aanval op je af en heb je de tegenkaart, dan vraagt het spel of je hem
    // inzet. De computer doet dat als er ook echt iets te verliezen valt: een Vuurduivel
    // zonder gebouwen om te raken is geen reden om je Bisschop weg te gooien.
    if (pending.kind === 'kies-tegenkaart') {
      const ik = state.players[seat];
      const teVerliezen =
        pending.attack === 'Rooftocht'
          ? ik.principality.landscapes.some((t) => t.resources > 0)
          : pending.attack === 'Vuurduivel'
            ? destructibleBuildings(ik).length > 0
            : knightsOf(ik).length > 0;
      const zet: Action = { type: 'tegenkaart', inzetten: teVerliezen };
      return isLegalAction(state, seat, zet).ok ? [zet] : [];
    }

    // De Verkenner: gratis en altijd beter dan blind trekken, dus die zet hij in.
    if (pending.kind === 'verkenner-vraag') {
      const zet: Action = { type: 'verkenner', inzetten: true };
      return isLegalAction(state, seat, zet).ok ? [zet] : [];
    }

    // En dan de landschappen uitzoeken: elke plek in de stapel komt in aanmerking.
    if (pending.kind === 'verkenner-kies') {
      for (let i = 0; i < state.landscapeDeck.length; i++) {
        kandidaten.push({ type: 'kies', column: i, side: 'boven' });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    // Bij Conflict wijs je handkaarten aan; de kolom draagt dan de index in de hand.
    if (pending.kind === 'kies-handkaarten') {
      const hand = state.players[pending.from].hand;
      for (let i = 0; i < hand.length; i++) {
        kandidaten.push({ type: 'kies', column: i, side: 'boven' });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    // Te veel kaarten in de hand: eerst kies je de kaart, dan de stapel waaronder hij
    // verdwijnt.
    if (pending.kind === 'kies-afleggen') {
      for (let i = 0; i < player.hand.length; i++) {
        kandidaten.push({ type: 'kies', column: i, side: 'boven' });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }
    // Bouwmeester loopt in drie stappen: stapel, kaart, kaart teruggeven.
    if (pending.kind === 'bouwmeester') {
      const opties =
        pending.stap === 'stapel'
          ? state.stacks.length
          : pending.stap === 'kaart'
            ? (state.stacks[pending.stack ?? -1]?.length ?? 0)
            : player.hand.length;
      for (let i = 0; i < opties; i++) {
        kandidaten.push({ type: 'kies', column: i, side: 'boven' });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    // Je startkaarten kiezen: de computer pakt gewoon de bovenste van de stapel.
    if (pending.kind === 'kies-startkaarten') {
      const stapel = state.stacks[pending.stack] ?? [];
      for (let i = 0; i < stapel.length; i++) {
        kandidaten.push({ type: 'kies', column: i, side: 'boven' });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    // Je hebt een stapel doorgekocht; kies er nu een kaart uit.
    if (pending.kind === 'kies-uit-stapel') {
      const stapel = state.stacks[pending.stack] ?? [];
      for (let i = 0; i < stapel.length; i++) {
        kandidaten.push({ type: 'kies', column: i, side: 'boven' });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    if (pending.kind === 'kies-afleg-stapel') {
      for (let i = 0; i < state.stacks.length; i++) {
        kandidaten.push({ type: 'kies', column: i, side: 'boven' });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    // De Spion kiest een kaart uit de hand van de tegenstander; de kolom draagt de index.
    if (pending.kind === 'spion') {
      const hand = state.players[pending.from].hand;
      for (let i = 0; i < hand.length; i++) {
        kandidaten.push({ type: 'kies', column: i, side: 'boven' });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    // Bij Burgeroorlog wijs je een gebouwde ridder of vloot aan bij je tegenstander.
    if (pending.kind === 'kies-terugname') {
      // Ridders en vloten bij de Burgeroorlog, gebouwen bij een mislukte Vuurduivel —
      // `terugnameKandidaten` is de ene plek die dat beslist.
      for (const plek of terugnameKandidaten(state.players[pending.from], pending.soort)) {
        kandidaten.push({ type: 'kies', column: plek.column, side: plek.side });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    // "Neem 1 of 2" — zodra er één gepakt is mag de Handelaar ook stoppen; de filter
    // onderaan laat de zet alleen door wanneer hij echt wettig is.
    if (pending.kind === 'handelaar') {
      kandidaten.push({ type: 'kies-klaar' });
    }

    // Een nieuw dorp: de eerste getrokken kaart kan boven of onder komen.
    if (pending.kind === 'leg-landschap') {
      for (const side of ['boven', 'onder'] as const) {
        kandidaten.push({ type: 'kies', column: pending.column, side });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }

    // De overige keuzes wijzen een landschap aan.
    const bron =
      pending.kind === 'kies-buit' || pending.kind === 'handelaar' ? pending.from : seat;
    for (const tile of state.players[bron].principality.landscapes) {
      if (
        pending.kind === 'plaats-buit' &&
        LANDSCAPE_RESOURCE[tile.landscape] !== pending.resource
      ) {
        continue;
      }
      kandidaten.push({ type: 'kies', column: tile.column, side: tile.side });
    }
    return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
  }

  // De opzetfase kent geen beurten: beide spelers richten tegelijk hun vorstendom in.
  // Deze tak moet dus vóór de beurtcontrole hieronder staan, anders komt de computer er
  // nooit aan toe en begint hij het spel met een lege hand.
  if (state.phase === 'opzet') {
    if (player.hand.length === 0) {
      for (let i = 0; i < state.stacks.length; i++) {
        kandidaten.push({ type: 'kies-startstapel', stack: i });
      }
      return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
    }
    const zet: Action = { type: 'opzet-klaar' };
    return isLegalAction(state, seat, zet).ok ? [zet] : [];
  }

  // Ligt er een aanbod van de tegenstander, dan hoort daar eerst een antwoord op — ja of
  // nee, allebei zijn ze wettig. Zonder dit blijft een aanbod eeuwig openstaan en loopt
  // het potje vast.
  //
  // Deze tak stond eerst ná de beurtcontrole hieronder, en dat was precies verkeerd om:
  // een aanbod doe je in je éígen beurt, dus de antwoorder is per definitie níét aan de
  // beurt — en kreeg dus een lege lijst terug, alsof hij niets mocht.
  //
  // Wélk antwoord verstandig is, staat hier bewust niet: dat is een afweging, en die
  // hoort bij wie de zet doet. De computer maakt hem in `packages/ai`, de mens met de
  // knoppen op zijn scherm.
  if (state.offer !== undefined && state.offer.from !== seat) {
    return ([true, false] as const)
      .map((accepteren): Action => ({ type: 'ruil-antwoord', accepteren }))
      .filter((a) => isLegalAction(state, seat, a).ok);
  }

  if (seat !== state.turn) return [];

  kandidaten.push({ type: 'gooi' }, { type: 'beurt-beeindigen' });

  for (const column of roadSpots(player.principality)) {
    const payment = findPayment(player, costOf('Straat'));
    if (payment) kandidaten.push({ type: 'bouw-straat', column, payment });
  }
  for (const column of settlementSpots(player.principality)) {
    const payment = findPayment(player, costOf('Dorp'));
    if (payment) kandidaten.push({ type: 'bouw-dorp', column, payment });
  }
  for (const building of player.principality.buildings) {
    if (building.kind !== 'dorp') continue;
    const payment = findPayment(player, costOf('Stad'));
    if (payment) kandidaten.push({ type: 'bouw-stad', column: building.column, payment });
  }
  for (let i = 0; i < state.stacks.length; i++) {
    kandidaten.push({ type: 'pak-kaart', stack: i });
    // Zit je hand vol, dan mag je er één onder een stapel schuiven en daarna pas aanvullen
    // (handboek p. 10). `isLegalAction` weegt of het nú mag; hier bieden we alleen de
    // mogelijkheid aan. Anders zag het scherm hem nooit in `legalActions` staan.
    kandidaten.push({ type: 'ruil-af', handIndex: 0, stack: i });
  }

  // Uitbreidingskaarten uit de hand in het vorstendom bouwen. Dat is waar het spel om
  // draait: ridders, vloten en gebouwen komen alleen zo op tafel.
  for (const cardId of player.hand) {
    const kaart = BASIC_CARDS.find((c) => c.id === cardId);
    if (kaart === undefined || kaart.kind !== 'uitbouw') continue;
    const payment = findPayment(player, kaart.cost);
    if (payment === undefined) continue;
    for (const bouwwerk of player.principality.buildings) {
      if (kaart.placement === 'alleen-stad' && bouwwerk.kind !== 'stad') continue;
      for (const side of ['boven', 'onder'] as const) {
        kandidaten.push({
          type: 'bouw-uitbreiding',
          cardId,
          column: bouwwerk.column,
          side,
          payment,
        });
      }
    }
  }

  // Ruilen met de bank: per grondstofsoort waarvan je er genoeg hebt, en per soort die je
  // ervoor terug kunt krijgen. Vroeger stond hier alleen het eerste landschap dat toevallig
  // ruimte had — dan hing het van de volgorde van je vorstendom af wát je terugkreeg, en
  // niet van wat je nodig had. Nu liggen alle mogelijkheden op tafel en kiest de weging.
  for (const optie of tradableWithBank(player, tradeAbilitiesOf(player))) {
    const bron = player.principality.landscapes
      .filter((t) => LANDSCAPE_RESOURCE[t.landscape] === optie.resource && t.resources > 0)
      .flatMap((t) => Array.from({ length: t.resources }, () => ({ column: t.column, side: t.side })))
      .slice(0, optie.rate);
    if (bron.length < optie.rate) continue;
    const gezien = new Set<Resource>();
    for (const doel of player.principality.landscapes) {
      const soort = LANDSCAPE_RESOURCE[doel.landscape];
      if (doel.resources >= 3 || soort === optie.resource || gezien.has(soort)) continue;
      gezien.add(soort);
      kandidaten.push({
        type: 'ruil-bank',
        give: bron,
        receive: { column: doel.column, side: doel.side },
      });
    }
  }

  // Actiekaarten uit de hand. Elke kaart draagt zijn eigen doelwit mee.
  for (const cardId of player.hand) {
    const naam = actionCardName(cardId);
    if (naam === undefined || isCounterOnly(naam)) continue;
    for (const target of targetsFor(state, seat, naam)) {
      kandidaten.push({ type: 'speel-actiekaart', cardId, ...(target ? { target } : {}) });
    }
  }

  return kandidaten.filter((a) => isLegalAction(state, seat, a).ok);
}

/** De doelwitten die voor deze kaart in aanmerking komen (undefined = geen nodig). */
function targetsFor(state: GameState, seat: Seat, naam: string): (CardTarget | undefined)[] {
  const tegen: Seat = seat === 0 ? 1 : 0;
  const ander = state.players[tegen];

  switch (naam) {
    case 'Alchimist':
      return [1, 2, 3, 4, 5, 6].map((value) => ({ kind: 'worp', value }) as CardTarget);

    // De Karavaan en de Handelaar openen een vraag: wélke grondstoffen wijst de speler
    // daarna zelf aan, stap voor stap. Geen doelwit bij de zet dus; of de kaart überhaupt
    // mag (twee grondstoffen bezitten, iets haalbaars bij de ander) bewaakt
    // `isLegalAction`.
    case 'Karavaan':
    case 'Handelaar':
      return [undefined];

    case 'Spion':
      return ander.hand.map((_, index) => ({ kind: 'handkaart', index }) as CardTarget);

    // Een aanval zonder doelwit is een weggegooide kaart: geen enkel gebouw of ridder
    // om te raken betekent dat de kaart niet gespeeld hoort te worden.
    case 'Vuurduivel':
      return destructibleBuildings(ander).map(
        (d) => ({ kind: 'gebouw', column: d.column, side: d.side }) as CardTarget,
      );

    case 'Zwarte ridder':
      return knightsOf(ander).map(
        (d) => ({ kind: 'gebouw', column: d.column, side: d.side }) as CardTarget,
      );

    case 'Rooftocht':
      // Stelen bij iemand die niets heeft is net zo zinloos.
      return ander.principality.landscapes.some((t) => t.resources > 0) ? [undefined] : [];

    default:
      return [undefined];
  }
}
